import type { AdminOverviewDto } from "@shelflist/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BackfillAction } from "@/components/jobs/backfill-action";
import { Page } from "@/components/page";
import { LoadingState } from "@/components/states";
import { type DotStatus, StatusDot } from "@/components/status-dot";
import { LINK_CARD_SLOT, LinkCard, StretchedLink } from "@/components/stretched-link";
import { Card, CardContent } from "@/components/ui/card";
import { adminKeys, adminOverviewQuery } from "@/lib/admin";
import { useErrorMessage } from "@/lib/errors";
import { backfillStatusQuery, jobKeys, startDigestBackfill, useJobTypeLabel } from "@/lib/jobs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/")({
  component: AdminOverviewPage,
});

const BACKFILL_JOB_TYPE = "koreader_digest_backfill";

type CardLink =
  | "/admin/users"
  | "/admin/invitations"
  | "/admin/jobs"
  | "/admin/email"
  | "/admin/settings"
  | "/admin/author-mapping"
  | "/upload/inbox";

/**
 * Status card: dot with title, one key figure and one sentence. The whole card is one link target
 * (a `StretchedLink` over the `LinkCard`); nested links use `LINK_CARD_SLOT`.
 */
function OverviewCard({
  title,
  to,
  status,
  figure,
  sentence,
  search,
  children,
}: {
  title: string;
  to: CardLink;
  /** Search params of the target (typed loosely: `to` is a union). */
  search?: Record<string, string>;
  status: DotStatus;
  figure: ReactNode;
  sentence: ReactNode;
  children?: ReactNode;
}) {
  return (
    <LinkCard
      data-testid="overview-card"
      data-status={status}
      className="hover:bg-muted/40 has-[a[data-card-link]:focus-visible]:ring-ring gap-2 py-4 transition-colors has-[a[data-card-link]:focus-visible]:ring-2"
    >
      <CardContent className="grid gap-2">
        <div className="flex items-center gap-2">
          <StatusDot status={status} />
          <h2 className="text-sm font-medium">
            <StretchedLink
              to={to}
              search={search as never}
              data-card-link
              className="inline-flex items-center gap-1 after:rounded-xl"
            >
              {title}
              <ChevronRight className="text-muted-foreground size-4" aria-hidden />
            </StretchedLink>
          </h2>
        </div>
        <p className="text-2xl leading-tight font-semibold">{figure}</p>
        <p className="text-muted-foreground text-sm">{sentence}</p>
        {children}
      </CardContent>
    </LinkCard>
  );
}

export function AdminOverviewPage() {
  const { t } = useTranslation("adminOverview");
  const { data, error } = useQuery(adminOverviewQuery);
  return (
    <Page title={t("title")} description={t("description")}>
      {error && !data ? (
        <p role="alert" className="text-destructive">
          {t("loadError")}
        </p>
      ) : !data ? (
        <LoadingState />
      ) : (
        <Overview overview={data} />
      )}
    </Page>
  );
}

type Overview = AdminOverviewDto;

const ACTIVE_JOB_ROWS = 3;

function Overview({ overview }: { overview: Overview }) {
  const { t } = useTranslation("adminOverview");
  const typeLabel = useJobTypeLabel();
  const { users, invitations, jobs, uploads, email, metadata, authorMapping } = overview;
  const mappingOff = !authorMapping.enabled || !authorMapping.lookupsAvailable;
  const activeCount = jobs.running + jobs.queued;
  const shownJobs = jobs.active.slice(0, ACTIVE_JOB_ROWS);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <OverviewCard
          title={t("users.title")}
          to="/admin/users"
          status="ok"
          figure={t("users.total", { count: users.total })}
          sentence={
            <>
              {t("users.roleAdmin", { count: users.byRole.admin })} ·{" "}
              {t("users.roleEditor", { count: users.byRole.editor })} ·{" "}
              {t("users.roleReader", { count: users.byRole.reader })}
              {users.disabled > 0 ? ` · ${t("users.disabled", { count: users.disabled })}` : ""}
            </>
          }
        />

        <OverviewCard
          title={t("invitations.title")}
          to="/admin/invitations"
          status="ok"
          figure={t("invitations.pending", { count: invitations.pending })}
          sentence={t("invitations.sentence")}
        />

        <OverviewCard
          title={t("jobs.title")}
          to="/admin/jobs"
          status={jobs.failedRecently > 0 ? "error" : "ok"}
          figure={
            activeCount > 0
              ? `${t("jobs.running", { count: jobs.running })} · ${t("jobs.queued", { count: jobs.queued })}`
              : t("jobs.idleFigure")
          }
          sentence={
            jobs.failedRecently > 0 ? (
              <Link
                to="/admin/jobs"
                search={{ status: "failed" }}
                className={cn(
                  LINK_CARD_SLOT,
                  "text-destructive font-medium underline-offset-4 hover:underline",
                )}
              >
                {t("jobs.failed", { count: jobs.failedRecently })}
              </Link>
            ) : (
              t("jobs.none")
            )
          }
        >
          {shownJobs.length > 0 ? (
            <ul aria-label={t("jobs.active")} className="grid gap-2">
              {shownJobs.map((job) => (
                <li key={job.id} className="grid gap-1 text-sm">
                  <Link
                    to="/admin/jobs/$jobId"
                    params={{ jobId: job.id }}
                    className={cn(
                      LINK_CARD_SLOT,
                      "w-fit font-medium underline-offset-4 hover:underline",
                    )}
                  >
                    {typeLabel(job.type)}
                  </Link>
                  <JobProgress progress={job.progress} />
                </li>
              ))}
              {jobs.active.length > shownJobs.length ? (
                <li className="text-muted-foreground text-xs">
                  {t("jobs.more", { count: jobs.active.length - shownJobs.length })}
                </li>
              ) : null}
            </ul>
          ) : null}
        </OverviewCard>

        <OverviewCard
          title={t("email.title")}
          to="/admin/email"
          status={email.configured ? "ok" : "off"}
          figure={email.configured ? t("email.configured") : t("email.notConfigured")}
          sentence={email.configured ? t("email.sentenceOn") : t("email.hint")}
        />

        <OverviewCard
          title={t("metadata.title")}
          to="/admin/settings"
          status={metadata.enabled ? "ok" : "off"}
          figure={
            metadata.enabled
              ? t("metadata.onFigure", { count: metadata.providers.length })
              : t("metadata.off")
          }
          sentence={
            metadata.enabled
              ? t("metadata.providers", {
                  list: metadata.providers.map((id) => t(`metadata.names.${id}`)).join(", "),
                })
              : t("metadata.offHint")
          }
        />

        <OverviewCard
          title={t("authorMapping.title")}
          to="/admin/author-mapping"
          search={{ status: "unclear" }}
          status={
            mappingOff
              ? "off"
              : authorMapping.unclear > 0 || authorMapping.suggestions > 0
                ? "warn"
                : "ok"
          }
          figure={
            mappingOff
              ? t("authorMapping.offFigure")
              : t("authorMapping.figure", { count: authorMapping.unclear })
          }
          sentence={
            mappingOff
              ? t("authorMapping.offHint")
              : t("authorMapping.sentence", {
                  unclear: authorMapping.unclear,
                  assigned: authorMapping.assigned,
                  unchecked: authorMapping.unchecked,
                })
          }
        >
          {!mappingOff && authorMapping.suggestions > 0 ? (
            <Link
              to="/admin/author-mapping"
              search={{ view: "suggestions" }}
              className={cn(
                LINK_CARD_SLOT,
                "w-fit text-sm font-medium underline-offset-4 hover:underline",
              )}
            >
              {t("authorMapping.suggestions", { count: authorMapping.suggestions })}
            </Link>
          ) : null}
        </OverviewCard>

        <OverviewCard
          title={t("uploads.title")}
          to="/upload/inbox"
          status={uploads.pending > 0 ? "warn" : "ok"}
          figure={
            uploads.pending > 0
              ? t("uploads.figure", { count: uploads.pending })
              : t("uploads.noneFigure")
          }
          sentence={uploads.pending > 0 ? t("uploads.sentence") : t("uploads.none")}
        />
      </div>

      <Maintenance overview={overview} />
      <p className="text-muted-foreground text-xs">{t("version", { version: overview.version })}</p>
    </>
  );
}

function JobProgress({
  progress,
}: {
  progress: { processed: number; total: number | null } | null;
}) {
  const { t } = useTranslation("adminOverview");
  if (!progress) return null;
  const { processed, total } = progress;
  const percent = total ? Math.min(100, Math.round((processed / total) * 100)) : null;
  return (
    <div className="grid gap-1">
      {percent !== null ? (
        <div
          role="progressbar"
          aria-label={t("jobs.progress", { processed, total })}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
        >
          <div className="bg-primary h-full transition-[width]" style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      <span className="text-muted-foreground text-xs">
        {total ? t("jobs.progress", { processed, total }) : t("jobs.progressCount", { processed })}
      </span>
    </div>
  );
}

/** Instance-wide actions that an admin starts by hand. Currently: the KOReader digest backfill. */
function Maintenance({ overview }: { overview: Overview }) {
  const { t } = useTranslation(["adminOverview", "jobs"]);
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const backfill = useQuery(backfillStatusQuery);
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  const activeFromOverview = overview.jobs.active.find((job) => job.type === BACKFILL_JOB_TYPE)?.id;
  const activeJobId = activeFromOverview ?? backfill.data?.activeJobId ?? null;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adminKeys.overview }),
      queryClient.invalidateQueries({ queryKey: jobKeys.all }),
    ]);
  const start = useMutation({
    mutationFn: startDigestBackfill,
    meta: { handlesErrors: true },
    onSuccess: async ({ jobId }) => {
      setStartedJobId(jobId);
      toast.success(t("jobs:backfill.started"));
      await refresh();
    },
    onError: async (error) => {
      // A 409 means a backfill is already queued or running: refetch so its link shows up.
      toast.error(errorMessage(error));
      await refresh();
    },
  });

  const files = backfill.data?.filesWithoutDigest;
  return (
    <section aria-labelledby="maintenance-title" className="grid gap-3">
      <h2 id="maintenance-title" className="text-lg font-semibold">
        {t("maintenance.title")}
      </h2>
      <Card className="py-4">
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div className="grid min-w-0 flex-1 gap-0.5 sm:basis-64">
            <h3 className="font-medium">{t("maintenance.backfillTitle")}</h3>
            <p className="text-muted-foreground text-sm">{t("maintenance.backfillDescription")}</p>
            {activeJobId ? (
              <p role="status" className="text-sm font-medium">
                {t("maintenance.inProgress")}
              </p>
            ) : null}
            {!activeJobId && startedJobId ? (
              <Link
                to="/admin/jobs/$jobId"
                params={{ jobId: startedJobId }}
                className="w-fit text-sm underline-offset-4 hover:underline"
              >
                {t("maintenance.openJob")}
              </Link>
            ) : null}
          </div>
          <BackfillAction
            filesWithoutDigest={files}
            activeJobId={activeJobId}
            starting={start.isPending}
            onStart={() => start.mutate()}
          />
        </CardContent>
      </Card>
    </section>
  );
}
